import { Theme } from "@radix-ui/themes";
import { router, usePage } from "@inertiajs/react";
import { Dialog } from "radix-ui";
import classNames from "classnames";
import { useEffect, useState, type ReactNode } from "react";
import type { SharedProps } from "@/types";
import TopBar from "@/shared/nav/TopBar";
import BrandPreviewBar from "@/shared/nav/BrandPreviewBar";
import BrandFooter from "@/shared/nav/BrandFooter";
import AppSidebar from "@/shared/nav/AppSidebar";
import AppTheme from "@/layouts/AppTheme";

// Per-viewer convenience only, so it lives in localStorage — which can throw
// (private windows, blocked site data), hence the try/catch around both sides.
const COLLAPSED_KEY = "driftables:sidebar-collapsed";

export default function Layout({ children }: { children: ReactNode }) {
  const { props } = usePage<SharedProps>();
  // A branded site has no sidebar: everything a customer does there is in
  // the top bar (TopBar), and the rest of the sidebar is pages it leaves out.
  const signedIn = Boolean(props.currentUser) && !props.brand;
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Read after mount rather than in the initializer, so the first render
  // matches the server's.
  useEffect(() => {
    try {
      setCollapsed(localStorage.getItem(COLLAPSED_KEY) === "1");
    } catch {
      // Expanded it is.
    }
  }, []);

  // The layout persists across Inertia visits, so the drawer has to be told
  // a link in it was followed.
  useEffect(() => router.on("navigate", () => setDrawerOpen(false)), []);

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    try {
      localStorage.setItem(COLLAPSED_KEY, next ? "1" : "0");
    } catch {
      // Remembered for this page view only.
    }
  };

  return (
    <AppTheme hasBackground={false}>
      <BrandPreviewBar />
      <TopBar onOpenMenu={signedIn ? () => setDrawerOpen(true) : undefined} />

      {signedIn ? (
        <div className="flex">
          <aside
            className={classNames(
              // No overflow here: AppSidebar scrolls itself, and the collapse
              // button hangs over this edge. z-20 keeps it above the page.
              "sticky top-14 z-20 hidden h-[calc(100vh-3.5rem)] shrink-0 border-r border-gray6 bg-panel md:block",
              collapsed ? "w-16" : "w-60",
            )}
          >
            <AppSidebar collapsed={collapsed} onToggle={toggleCollapsed} />
          </aside>
          <main className="min-w-0 flex-1">{children}</main>
        </div>
      ) : (
        <main>{children}</main>
      )}

      <BrandFooter />

      {signedIn && (
        <Dialog.Root open={drawerOpen} onOpenChange={setDrawerOpen}>
          <Dialog.Portal>
            {/* The portal renders outside the page's Theme, and the sidebar's
                Radix components need one. Bare: it inherits AppTheme's. */}
            <Theme>
              <Dialog.Overlay className="fixed inset-0 z-40 bg-scrim data-[state=open]:animate-fadeIn md:hidden" />
              <Dialog.Content
                aria-describedby={undefined}
                className="fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] bg-panel shadow-xl data-[state=open]:animate-enterFromLeft md:hidden"
              >
                <Dialog.Title className="sr-only">Menu</Dialog.Title>
                <AppSidebar />
              </Dialog.Content>
            </Theme>
          </Dialog.Portal>
        </Dialog.Root>
      )}
    </AppTheme>
  );
}
